package com.sddamico.swigmore.models

import kotlinx.serialization.Serializable

/** A league team. [id] is the Fantrax team id, which ballots reference. */
@Serializable
data class LeagueTeam(
  val id: String,
  val name: String,
  val shortName: String? = null,
)

@Serializable
data class TeamRecord(val wins: Int, val losses: Int, val ties: Int) {
  val games: Int get() = wins + losses + ties

  /** Ties count half. */
  val winShare: Double get() = (wins + ties / 2.0) / games

  /** "6-0", or "5-0-1" only when there is a tie, matching how the design prints records. */
  override fun toString() = if (ties > 0) "$wins-$losses-$ties" else "$wins-$losses"
}

/** A team's record as of [week]. */
@Serializable
data class TeamStanding(
  val teamId: String,
  val week: Int,
  val record: TeamRecord,
  val pointsFor: Double,
  val pointsPerGame: Double?,
  val standingsRank: Int,
  val gamesBack: Double,
)

/** One side of a game. [record] and [pointsPerGame] are as of the game's week; null before any sync. */
@Serializable
data class MatchupTeam(
  val teamId: String,
  val name: String,
  val shortName: String? = null,
  val record: TeamRecord? = null,
  val pointsPerGame: Double? = null,
)

/**
 * Fantrax points once the game's week is released; not [final] until its scoring period ends.
 * [winnerTeamId] is null on a tie.
 */
@Serializable
data class MatchupScore(val home: Double, val away: Double, val final: Boolean, val winnerTeamId: String?)

/** [score] is null before the week's scores are released or while the game hasn't started. */
@Serializable
data class LeagueMatchup(val week: Int, val home: MatchupTeam, val away: MatchupTeam, val score: MatchupScore?)

@Serializable
enum class FantraxSyncState { RUNNING, SUCCEEDED, FAILED }

/** The latest sync attempt, plus the latest one that worked. Times are epoch milliseconds. */
@Serializable
data class FantraxSyncStatus(
  val currentWeek: Int? = null,
  val lastSuccessAt: Long? = null,
  val lastAttemptAt: Long? = null,
  val lastAttemptState: FantraxSyncState? = null,
  val lastError: String? = null,
  val running: Boolean = false,
)

/** `GET /api/league`: teams, standings as of [standingsWeek], and sync freshness. */
@Serializable
data class LeagueResponse(
  val currentWeek: Int?,
  val standingsWeek: Int?,
  val teams: List<LeagueTeam>,
  val standings: List<TeamStanding>,
  val sync: FantraxSyncStatus,
)

/** `GET /api/league/matchups?week=N` (defaults to the current week). */
@Serializable
data class MatchupsResponse(val week: Int?, val matchups: List<LeagueMatchup>)

enum class GameOutcome { WIN, LOSS, TIE }

/** One side of a scored game; [record] includes this game, and [standing] is the team's place in that week's poll. */
@Serializable
data class ResultTeam(
  val teamId: String,
  val name: String,
  val score: Double,
  val outcome: GameOutcome,
  val record: TeamRecord,
  val standing: PollStanding,
)

/** [final] is false while Fantrax can still correct the scores. */
@Serializable
data class GameResultDto(val week: Int, val home: ResultTeam, val away: ResultTeam, val final: Boolean)

/** A team's games against one opponent; a tie counts as neither a win nor a loss. */
@Serializable
data class MeetingsDto(val opponent: LeagueTeam, val wins: Int, val losses: Int, val ties: Int)

@Serializable
data class HeadToHeadDto(val team: LeagueTeam, val opponents: List<MeetingsDto>)

/** `GET /api/league/results`: every scored game this season, in week order, and each team's head-to-head. */
@Serializable
data class GameResultsResponse(val games: List<GameResultDto>, val headToHead: List<HeadToHeadDto>)
